// One meter card on the home grid — web twin of the mobile MetricCard:
// name + brand-colored icon bubble, big value + unit, subtitle, alert pill.
// Dark mode tints the card with the meter's brand color (CSS --brand-rgb).
// Clicking selects the meter — the detail panel renders inline below the grid
// (the legacy portal's pattern), so this is a button, not a link.

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { CSSProperties } from 'react';
import { openAlerts, type MeterCard } from '../lib/api';
import { meterBrand } from '../lib/colors';
import { formatDateTime, formatValue } from '../lib/meterCharts';
import { useTheme } from '../lib/theme';
import Icon from './Icon';
import MdiIcon from './MdiIcon';

export default function MeterTile({
  meter,
  selected,
  onSelect,
  rangeLabel,
}: {
  meter: MeterCard;
  selected: boolean;
  onSelect: () => void;
  /** Localized label of the active period window (e.g. "Jun 1 – 8"). */
  rangeLabel: string;
}): ReactElement {
  const { t } = useTranslation();
  const { mode } = useTheme();
  const brand = meterBrand(meter.color);
  const dark = mode === 'dark';

  const iconColor = dark ? brand.onDark : brand.solid;
  const bubbleBg = dark ? `rgba(${brand.rgbString}, 0.22)` : brand.soft;

  // A sum-type meter with kind 'last' means the selected window had no
  // readings at all — the backend fell back to the meter's raw totaliser
  // index (its latest reading, outside the window). Consumption for the
  // period is 0; showing the index would read as a huge value. The legacy
  // portal showed 0 here too.
  const staleSum =
    meter.type === 'sum' && meter.kind === 'last' && meter.value !== null && meter.value !== undefined;
  const value = staleSum ? 0 : meter.value;

  let subtitle: string;
  if (value === null || value === undefined) {
    subtitle = t('home.noReadingYet');
  } else if (meter.kind === 'consumption' || staleSum) {
    subtitle = `${t('meter.consumption')} · ${rangeLabel}`;
  } else {
    subtitle = meter.updated_at
      ? t('home.lastReading', { date: formatDateTime(meter.updated_at) })
      : t('home.lastValue');
  }

  // Only alarms nobody has handled get the badge — what app.divako.io and the
  // mobile app count: alert level, neither acknowledged nor suppressed.
  const open = openAlerts(meter.alerts);
  const alerts = open.length;

  return (
    <button
      onClick={onSelect}
      className={`metric-card${selected ? ' selected' : ''}${alerts > 0 ? ' has-alert' : ''}`}
      aria-pressed={selected}
      style={{ '--brand-rgb': brand.rgbString } as CSSProperties}
    >
      {alerts > 0 && (
        <span className="alert-pill">
          <Icon name="alert-triangle" size={12} />
          <span className="alert-pill-text">
            {alerts === 1 ? open[0].label : t('home.activeAlerts', { count: alerts })}
          </span>
        </span>
      )}
      <div className="metric-head">
        <span className="metric-name">{meter.name}</span>
        <span className="icon-bubble" style={{ background: bubbleBg, color: iconColor }}>
          <MdiIcon name={meter.icon} size={20} />
        </span>
      </div>
      <div className="metric-value">
        {formatValue(value)}
        {value !== null && value !== undefined && (
          <span className="metric-unit">{meter.unit}</span>
        )}
      </div>
      <div className="metric-sub">{subtitle}</div>
    </button>
  );
}
